import type { JSX } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Icon } from '../primitives/Icon';
import { useModalStore } from '@/state/modalStore';
import { useSessionStore } from '@/state/sessionStore';
import { useTweaksStore } from '@/state/tweaksStore';
import { PATHS } from '@/routes/paths';
import { activeZone } from './Rail';

/**
 * Top bar.
 *
 * NAVY, not white. This supersedes the brand skill's white top bar per the PO's
 * ruling (OI-2, 2026-08-16); the skill update is still outstanding, so it is
 * flagged in docs/BRANDING.md rather than silently reconciled.
 */
export function TopBar({
  initials,
  userName,
}: {
  readonly initials: string;
  readonly userName: string;
}): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const showModal = useModalStore((s) => s.show);

  const search = useSessionStore((s) => s.search);
  const setSearch = useSessionStore((s) => s.setSearch);
  const setLock = useSessionStore((s) => s.setLock);
  const currency = useSessionStore((s) => s.currency);
  const cycleCurrency = useSessionStore((s) => s.cycleCurrency);
  const dateRange = useSessionStore((s) => s.dateRange);
  const cycleDateRange = useSessionStore((s) => s.cycleDateRange);
  const filterCount = useSessionStore((s) => s.filterCount);
  const showScopeFilters = useTweaksStore((s) => s.showScopeFilters);

  /**
   * Global search sends the user where the results actually live. Typing in the
   * bar from any screen lands them in the Lockerroom library with the term
   * applied, rather than filtering a screen that has nothing to filter.
   */
  const onSearch = (value: string): void => {
    setSearch(value);
    const inLockerroom = activeZone(pathname) === 'lockerroom';
    if (value && !inLockerroom) {
      setLock({ tab: 'library', q: value });
      void navigate(PATHS.lockerroom);
      return;
    }
    if (inLockerroom) setLock({ q: value });
  };

  return (
    <header className="pnet-topbar">
      <button
        type="button"
        className="tb-home"
        title="Home"
        onClick={() => {
          void navigate(PATHS.arena);
        }}
      >
        <Icon name="home" title="Home" />
      </button>

      <div className="tb-wrap">
        <Icon name="search" />
        <input
          className="tb-search"
          placeholder="Search agents, analyses, partners, research…"
          value={search}
          aria-label="Global search"
          onChange={(e) => {
            onSearch(e.target.value);
          }}
        />
      </div>

      <div className="tb-cluster">
        <button
          type="button"
          className="tb-ctl hide-sm"
          title="Date range"
          onClick={cycleDateRange}
        >
          <Icon name="calendar" />
          <span>{dateRange}</span>
        </button>
        <button type="button" className="tb-ctl" title="Currency" onClick={cycleCurrency}>
          <Icon name="euro" />
          <span>{currency}</span>
        </button>
        {/* The scope row is what the "Show scope filters row" tweak hides. The
            filters themselves stay applied — hiding the control must not
            silently change what the screens below are showing. */}
        {showScopeFilters && (
          <button
            type="button"
            className="tb-ctl hide-sm"
            title="More filters"
            onClick={() => {
              showModal('filters');
            }}
          >
            <Icon name="filter" />
            <span>More filters</span>
            {filterCount > 0 && <span className="tb-count">{filterCount}</span>}
          </button>
        )}
        {/* Tweaks used to sit here. It moved into Settings (rail, foot) so there
            is one place for preferences and one fewer control competing for the
            top bar on a narrow screen. */}
        <span className="tb-user" title={userName}>
          {initials}
        </span>
      </div>
    </header>
  );
}
